import type { LearnTopic } from "../topic-types";

/**
 * Trauma & Relationships.
 *
 * Cross-links heavily with Attachment Styles, Boundaries and Trauma Bonds
 * rather than duplicating them in full.
 */
export const TRAUMA_RELATIONSHIPS_TOPIC: LearnTopic = {
  slug: "trauma-relationships",
  emoji: "🤝",
  title: "Trauma & Relationships",
  tagline: "Connection after difficult experiences",
  theme: "gold",
  category: "relationships",
  seoDescription:
    "How trauma can shape trust, closeness and conflict in relationships — and practical, non-diagnostic guidance on communication, boundaries, red and green flags, and repair. From Recovery Trauma™.",
  cardBlurb: "Trust, closeness, conflict and repair — relationships after difficult experiences.",
  isNew: true,
  intro: [
    "Difficult or overwhelming experiences don't stay in the past — they can quietly shape how safe, close or guarded we feel in relationships years later.",
    "This section looks at how trauma can affect trust, fear of abandonment, fear of closeness and conflict, alongside practical tools for communication, boundaries and repair.",
    "If you want to go deeper into patterns of connection specifically, the Attachment Styles module explores this in more depth — the two sit well together.",
  ],
  closing:
    "This is educational material, not therapy or relationship advice tailored to your situation. If a relationship feels frightening or unsafe, please see our Crisis Resources — support is available beyond this page.",
  continueTo: [
    { label: "Attachment Styles", to: "/learn/attachment-styles" },
    { label: "Boundaries", to: "/learn/boundaries" },
    { label: "Trauma Bonds", to: "/learn/trauma-bonds" },
    { label: "Open your Journal", to: "/journal" },
  ],
  sections: [
    {
      slug: "understanding-trauma-and-connection",
      emoji: "📚",
      title: "Understanding trauma and connection",
      blurb: "How difficult experiences can shape trust, closeness and fear.",
      intro: "Education first — nothing here asks you to look at a specific relationship yet.",
      resources: [
        {
          slug: "trust-after-trauma",
          kind: "learn",
          title: "Trust after trauma",
          summary: "Why trust can feel effortful, slow to build, or oddly automatic.",
          minutes: 4,
          blocks: [
            {
              type: "prose",
              text: "Trust is often described as a feeling, but it behaves more like a prediction: based on past experience, what is my nervous system expecting from this person? When trust was broken by people who were supposed to be safe, that prediction can tilt toward caution — sometimes for good reason, sometimes long after the original situation has changed.",
            },
            {
              type: "list",
              title: "This can look like",
              items: [
                "Waiting for the other shoe to drop even when things are going well",
                "Testing people, consciously or not, before letting your guard down",
                "Trusting quickly and completely, then feeling betrayed by ordinary human imperfection",
                "Assuming the worst interpretation of an ambiguous message or silence",
              ],
            },
            {
              type: "prose",
              text: "If you want to explore how these patterns tend to cluster and why, the Attachment Styles module looks at this in far more depth — this section focuses on relationships more broadly.",
            },
          ],
        },
        {
          slug: "fear-of-abandonment",
          kind: "learn",
          title: "Fear of abandonment",
          summary: "When the possibility of being left feels like an emergency.",
          minutes: 4,
          blocks: [
            {
              type: "prose",
              text: "Fear of abandonment often develops where connection was inconsistent, conditional, or withdrawn as a form of punishment. The nervous system can learn to treat small signs of distance — a delayed reply, a change in tone — as evidence that abandonment is imminent.",
            },
            {
              type: "list",
              title: "You might recognise",
              items: [
                "A spike of panic when someone goes quiet",
                "Difficulty tolerating normal, healthy space in a relationship",
                "Seeking constant reassurance, which soothes only briefly",
                "Pre-emptively pulling away so that you leave before you can be left",
              ],
            },
          ],
        },
        {
          slug: "fear-of-closeness",
          kind: "learn",
          title: "Fear of closeness",
          summary: "When intimacy itself, not distance, feels like the danger.",
          minutes: 4,
          blocks: [
            {
              type: "prose",
              text: "For some people, trauma makes closeness the frightening part rather than distance. If vulnerability once led to harm, being truly seen can feel exposing even with someone safe and caring.",
            },
            {
              type: "list",
              title: "This can look like",
              items: [
                "Discomfort or numbness when a conversation turns emotionally close",
                "Finding reasons to create distance just as a relationship deepens",
                "Difficulty naming needs or feelings even when invited to",
                "Preferring the idea of connection to the reality of being fully known",
              ],
            },
            {
              type: "note",
              text: "Fear of abandonment and fear of closeness often coexist in the same person, sometimes in the same relationship, sometimes at the same moment.",
            },
          ],
        },
        {
          slug: "attachment-patterns-in-relationships",
          kind: "learn",
          title: "Attachment patterns in relationships",
          summary: "How earlier patterns of connection tend to show up with partners, friends and family.",
          minutes: 4,
          blocks: [
            {
              type: "prose",
              text: "The patterns learned early in life — how much closeness felt safe, how conflict was handled, whether needs were met — tend to resurface in adult relationships, often more strongly with people we are closest to.",
            },
            {
              type: "list",
              title: "Loose patterns some people notice",
              items: [
                "Anxious patterns: seeking reassurance, alert to small shifts in warmth",
                "Avoidant patterns: valuing independence, uneasy relying on others",
                "Disorganised patterns: wanting closeness and fearing it at once",
                "Secure patterns: broadly able to be close and separate without losing either",
              ],
            },
            {
              type: "note",
              text: "For a full, non-diagnostic guide to these patterns and how to build more security over time, see the Attachment Styles module — it is designed to be explored alongside this one.",
            },
          ],
        },
      ],
    },
    {
      slug: "conflict-and-communication",
      emoji: "💬",
      title: "Conflict, communication and boundaries",
      blurb: "Practical tools for speaking, disagreeing and holding limits.",
      intro: "These are skills, not personality traits — they tend to improve with gentle, repeated practice.",
      resources: [
        {
          slug: "conflict-after-trauma",
          kind: "learn",
          title: "Conflict after trauma",
          summary: "Why ordinary disagreement can feel disproportionately threatening.",
          minutes: 4,
          blocks: [
            {
              type: "prose",
              text: "If conflict in the past was unpredictable, frightening or unresolved, the nervous system can learn to treat any disagreement as danger — even a calm, minor one with someone safe.",
            },
            {
              type: "list",
              title: "Common responses to conflict",
              items: [
                "Shutting down or going quiet the moment tension rises",
                "Fighting hard to end the discomfort as quickly as possible",
                "Immediately conceding or apologising to restore calm",
                "Replaying the conflict for hours or days afterwards",
              ],
            },
            {
              type: "note",
              text: "These responses map loosely onto fight, flight, freeze and fawn — see our Trauma Responses guide if you would like to explore that further.",
            },
          ],
        },
        {
          slug: "communicating-clearly",
          kind: "practice",
          title: "Communicating clearly",
          summary: "Example phrasing for naming needs, concerns and limits directly.",
          minutes: 6,
          blocks: [
            {
              type: "prose",
              text: "Direct communication can feel exposing if indirect communication — hinting, withdrawing, testing — once felt safer. It tends to get easier with repetition rather than confidence arriving first.",
            },
            {
              type: "steps",
              title: "A structure you can borrow",
              items: [
                { label: "Describe the situation", note: "\"When plans change without much notice...\"" },
                { label: "Name the feeling", note: "\"...I notice I feel anxious.\"" },
                { label: "Say the need", note: "\"It would help me to get a heads-up when you can.\"" },
                { label: "Invite a response", note: "\"Is that something that could work for you?\"" },
              ],
            },
            {
              type: "list",
              title: "More example phrases",
              items: [
                "\"I want to understand — can you say a bit more about what you meant?\"",
                "\"I'm not ready to respond to that yet — can we come back to it later today?\"",
                "\"I noticed I went quiet just now. I'm still here, I just needed a moment.\"",
                "\"I don't think that will work for me, but I'd like to find something that does.\"",
              ],
            },
          ],
        },
        {
          slug: "boundaries-in-relationships",
          kind: "learn",
          title: "Boundaries in relationships",
          summary: "Boundaries as information about you, not a punishment of someone else.",
          minutes: 4,
          blocks: [
            {
              type: "prose",
              text: "A boundary describes what you will do, not a demand for someone else to change. It can be said warmly, adjusted as trust builds, and still feel uncomfortable to hold even when it is right.",
            },
            {
              type: "list",
              items: [
                "Boundaries protect a relationship as often as they protect an individual",
                "Guilt after setting one is common and does not mean it was wrong",
                "A relationship that cannot tolerate any boundaries is useful information",
              ],
            },
            {
              type: "note",
              text: "For a full module on identifying, stating and holding boundaries, see Boundaries — it pairs directly with the ideas here.",
            },
          ],
        },
        {
          slug: "repair-after-conflict",
          kind: "practice",
          title: "Repair after conflict",
          summary: "A step-by-step way to reconnect once things have settled.",
          minutes: 7,
          blocks: [
            {
              type: "prose",
              text: "Repair matters more than avoiding conflict altogether. Relationships that repair well after rupture often build more trust than ones that rarely disagree.",
            },
            {
              type: "steps",
              items: [
                { label: "Wait until both people are settled", note: "Repair rarely works while either person is still activated." },
                { label: "Name your part specifically", note: "Rather than a general \"sorry for everything.\"" },
                { label: "Share what you were protecting", note: "\"I went quiet because I felt overwhelmed, not because I didn't care.\"" },
                { label: "Ask what it was like for them", note: "And let the answer land before defending yourself." },
                { label: "Agree one small, specific change", note: "Something realistic enough to actually happen next time." },
              ],
            },
            {
              type: "note",
              text: "Repair is a skill both people build together over time. One good repair does not undo a pattern, and one missed repair does not ruin things either.",
            },
          ],
        },
      ],
    },
    {
      slug: "recognising-patterns",
      emoji: "🚦",
      title: "Recognising healthy and unhealthy patterns",
      blurb: "Red flags, green flags, and building emotional safety.",
      intro: "These are patterns to notice over time, not a checklist for judging a relationship in one sitting.",
      resources: [
        {
          slug: "red-flags-and-green-flags",
          kind: "reflect",
          title: "Red flags and green flags",
          summary: "Recognising patterns that tend to erode or build safety over time.",
          minutes: 8,
          blocks: [
            {
              type: "prose",
              text: "No single moment defines a relationship, but patterns that repeat over time are usually worth taking seriously — in either direction.",
            },
            {
              type: "list",
              title: "Possible red flags",
              items: [
                "Consistent dismissal or mockery of your feelings",
                "Isolation from friends, family or other support",
                "Apologies that are never followed by change",
                "Your boundaries being treated as a problem to overcome",
                "A pattern of intense highs followed by withdrawal or punishment",
                "Feeling like you are managing someone else's moods to stay safe",
              ],
            },
            {
              type: "list",
              title: "Possible green flags",
              items: [
                "Consistency between what is said and what actually happens",
                "Room to disagree without the relationship feeling at risk",
                "Repair being offered, not only accepted, after conflict",
                "Your no being received without a lasting cost",
                "Curiosity about your experience, rather than defensiveness",
                "Feeling more like yourself around this person, not less",
              ],
            },
            {
              type: "note",
              text: "If this reflection brings up a relationship that feels frightening or repeats a familiar, hard-to-leave pull, our Trauma Bonds module explores that dynamic directly, and Crisis Resources are available if you are not safe.",
            },
            {
              type: "prompts",
              title: "For reflection",
              items: [
                "Which of these patterns do I recognise in a current or past relationship?",
                "Which pattern took me longest to notice, and why?",
                "What would it look like to name one of these out loud?",
              ],
            },
          ],
        },
        {
          slug: "building-emotional-safety",
          kind: "learn",
          title: "Building emotional safety",
          summary: "What safety tends to be made of, in ordinary, repeated moments.",
          minutes: 4,
          blocks: [
            {
              type: "prose",
              text: "Emotional safety is rarely built in one grand gesture. It tends to accumulate through small, repeated experiences of being met with consistency, respect and care — the same ideas explored in more depth in the Attachment Styles module's Building Secure Attachment section.",
            },
            {
              type: "list",
              title: "Ingredients people often name",
              items: [
                "Predictability — words and actions matching over time",
                "Respect for a no, even an inconvenient one",
                "Room to be imperfect without losing the relationship",
                "Being asked, not just told, what you need",
              ],
            },
          ],
        },
      ],
    },
    {
      slug: "exercises-and-worksheets",
      emoji: "📄",
      title: "Exercises & worksheets",
      blurb: "A printable page for reflection away from a screen.",
      intro: "Print this, or copy the headings into your own journal.",
      resources: [
        {
          slug: "emotional-safety-worksheet",
          kind: "worksheet",
          title: "Emotional Safety in My Relationships",
          summary: "A structured page for reflecting on where safety is present or missing.",
          worksheetFields: [
            { label: "A relationship I want to reflect on (no need to name anyone)", lines: 1 },
            { label: "Moments this relationship has felt safe", lines: 3 },
            { label: "Moments it has felt unsafe or uncertain", lines: 3 },
            { label: "Red flags I notice, if any", lines: 3 },
            { label: "Green flags I notice, if any", lines: 3 },
            { label: "How conflict tends to go between us", lines: 3 },
            { label: "One thing I would like to try communicating more directly", lines: 2 },
            { label: "One boundary that would help me feel safer", lines: 2 },
          ],
        },
      ],
    },
  ],
};
